import { useStyletron } from '@thanx/uikit/theme'
import { getTiersConfig } from 'actions/config'
import { getPointsExperiences } from 'actions/pointsExperiences'
import SectionHeader from 'components/Navbar/Bar/SectionHeader'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import {
  formatPermissionReason,
  usePermissionLabel,
  usePermissionReason,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { selectTiersConfig } from 'selectors/config'
import { selectExperience } from 'selectors/pointsExperience'
import { useAbility } from 'utilities/ability'
import NavLink from '../Bar/NavLink'

const t = buildTranslate('nav.loyalty_menu')

type PropsT = {
  isLeftNav?: boolean
  onClick?: () => void
}

const LoyaltyContent: React.FC<PropsT> = ({
  isLeftNav = false,
  onClick = () => {},
}) => {
  const ability = useAbility()
  const pointsExperience = useSelector(selectExperience)
  const tiers = useSelector(selectTiersConfig)
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const rbacEnabled = useFlag('role-based-access-control', false)
  const loyaltySetupDisabled = rbacEnabled
    ? ability.cannot('access', 'LoyaltySetup')
    : ability.cannot('manage', 'LegacyReports')
  const loyaltySetupReason = usePermissionReason(
    'LoyaltySetup',
    loyaltySetupDisabled
  )

  const pointsPermissionDenied = ability.cannot('access', 'Points')
  const pointsArchived = pointsExperience?.state === 'archived'
  const pointsNotConfigured = !pointsExperience || pointsArchived
  const pointsDisabled = pointsPermissionDenied || pointsNotConfigured
  const pointsLabel = usePermissionLabel('PointsMultipliers')

  const tiersDisabled = ability.cannot('access', 'Tiers')
  const tiersReason = usePermissionReason('TiersProgram', tiersDisabled)

  // Legacy: manage all covers points/tiers. RBAC: check specific subjects.
  // Either way, actually check the ability — never default to true.
  const canAccessPoints =
    ability.can('access', 'Points') || ability.can('manage', 'Points')
  const canAccessTiers =
    ability.can('access', 'Tiers') || ability.can('manage', 'Tiers')

  // Guard fetches: the navbar fetches /points_experiences only for roles that
  // can use the Points section (the customer-profile page fetches it
  // separately), and /tiers_config requires `tiers_program`.
  const canFetchPoints = canAccessPoints
  const canFetchTiers = canAccessTiers

  const hasFetchedPoints = useRef(false)
  useEffect(() => {
    if (canFetchPoints && !pointsExperience && !hasFetchedPoints.current) {
      hasFetchedPoints.current = true
      dispatch(getPointsExperiences())
    }
    if (canFetchTiers && !tiers) {
      dispatch(getTiersConfig())
    }
  }, [dispatch, pointsExperience, tiers, canFetchPoints, canFetchTiers])

  // Only show reason after we've attempted to fetch, to avoid flashing
  // the tooltip during initial load.
  const getPointsReason = () => {
    if (!hasFetchedPoints.current && !pointsPermissionDenied) return undefined
    const featureReason = pointsArchived
      ? t('points_archived')
      : pointsNotConfigured
      ? t('points_not_configured')
      : undefined
    if (pointsPermissionDenied) {
      const suffix = featureReason ? `+ ${featureReason}` : undefined
      return formatPermissionReason(pointsLabel, true, suffix)
    }
    return featureReason
  }
  const pointsReason = getPointsReason()

  return (
    <>
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('points')}
        link="/points"
        disabled={pointsDisabled}
        disabledReason={pointsReason}
      />

      {(rbacEnabled || ability.can('manage', 'all')) && tiers && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('tiers')}
          link="/reports/tiers"
          disabled={tiersDisabled}
          disabledReason={tiersReason}
        />
      )}
      <div
        className={`${isLeftNav ? 'mt-xs mx-m ' : ''}${css({
          border: `1px solid ${palette.grey10}`,
        })}`}
      >
        <SectionHeader
          isLeftNav={isLeftNav}
          text={I18n.t('nav.legacy.reports')}
          iconName="scroll"
          isLegacy
          learnMore={!isLeftNav}
        />
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('loyalty_setup')}
          link="/loyalty"
          disabled={loyaltySetupDisabled}
          disabledReason={loyaltySetupReason}
          isLegacy
        />
      </div>
    </>
  )
}

export default LoyaltyContent
